import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../PageTitle";
import CashDisbursementStaffNavigation from "./navigation/CashDisbursementStaffNavigation";
import useAxios from "../../../utilities/useAxios";
import {
	Table,
	Button,
	message,
	Layout,
	ConfigProvider,
	Space,
	Tag,
	Descriptions,
	Typography,
	Tooltip,
	Tabs,
} from "antd";
import locale from "antd/lib/locale/ko_KR";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import PageHeader from "../../common/PageHeader";

const { Content } = Layout;
const { Text } = Typography;

const STATUS_TABS = [
	{ key: "대기", label: "대기" },
	{ key: "승인", label: "승인" },
	{ key: "거절", label: "거절" },
];

const currencyLabel = (c) => (c === "USD" ? "달러" : "원");

const lineColumns = [
	{
		title: "통화",
		dataIndex: "currency",
		key: "currency",
		width: 72,
		render: (c) => currencyLabel(c),
	},
	{
		title: "적요",
		dataIndex: "subject",
		key: "subject",
		ellipsis: true,
	},
	{
		title: "금액",
		dataIndex: "amount",
		key: "amount",
		width: 120,
		align: "right",
		render: (v, row) =>
			`${Number(v).toLocaleString("ko-KR")} ${currencyLabel(row.currency)}`,
	},
	{
		title: "비고",
		dataIndex: "description",
		key: "description",
		ellipsis: true,
		render: (t) => t || "—",
	},
];

async function downloadVoucherAttachment(voucherId, attachmentId, filename) {
	const res = await useAxios.get(
		`utils/cash-disbursement/${voucherId}/attachments/${attachmentId}/download/`,
		{
			responseType: "blob",
			headers: { Accept: "application/octet-stream" },
		},
	);
	const blob = new Blob([res.data]);
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename || "download";
	document.body.appendChild(a);
	a.click();
	a.remove();
	window.URL.revokeObjectURL(url);
}

const statusTag = (s) => {
	if (s === "승인") return <Tag color="green">승인</Tag>;
	if (s === "거절") return <Tag color="red">거절</Tag>;
	return <Tag color="blue">대기</Tag>;
};

const rejectReasonTooltipOverlayStyle = {
	maxWidth: 420,
};

const rejectReasonTooltipInnerStyle = {
	backgroundColor: "#141414",
	color: "#fff",
	whiteSpace: "pre-wrap",
};

const MyCashDisbursementVouchers = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [statusTab, setStatusTab] = useState("대기");

	const fetchList = useCallback(async () => {
		setLoading(true);
		try {
			const res = await useAxios.get(
				"utils/cash-disbursement/",
			);
			setRows(res.data || []);
		} catch {
			message.error("목록을 불러오지 못했습니다.");
			setRows([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchList();
	}, [fetchList]);

	const filteredRows = useMemo(
		() =>
			(rows || []).filter(
				(r) => (r.status || "대기") === statusTab,
			),
		[rows, statusTab],
	);

	const columns = [
		{
			title: "작성일",
			dataIndex: "document_date",
			key: "document_date",
			width: 120,
		},
		{
			title: "적요",
			dataIndex: "lines",
			key: "lines",
			ellipsis: true,
			render: (lines) =>
				(lines || []).map((l) => l.subject).join(", "),
		},
		{
			title: "합계(원)",
			dataIndex: "total_amount_krw",
			key: "total_amount_krw",
			width: 112,
			align: "right",
			render: (v) =>
				v != null && v !== ""
					? `${Number(v).toLocaleString("ko-KR")} 원`
					: "—",
		},
		{
			title: "담당자",
			dataIndex: "person_in_charge",
			key: "person_in_charge",
			width: 88,
		},
		{
			title: "상태",
			dataIndex: "status",
			key: "status",
			width: 80,
			render: (v) => statusTag(v || "대기"),
		},
		{
			title: "거절 사유",
			dataIndex: "reject_reason",
			key: "reject_reason",
			width: 160,
			ellipsis: true,
			render: (text, record) => {
				if ((record.status || "대기") !== "거절") {
					return "—";
				}
				const s =
					typeof text === "string"
						? text.trim()
						: "";
				if (!s) {
					return "—";
				}
				const display =
					s.length > 48
						? `${s.slice(0, 48)}…`
						: s;
				return (
					<Tooltip
						title={s}
						placement="topLeft"
						overlayStyle={
							rejectReasonTooltipOverlayStyle
						}
						overlayInnerStyle={
							rejectReasonTooltipInnerStyle
						}>
						<span
							style={{
								cursor: "default",
								display: "inline-block",
								maxWidth: "100%",
							}}>
							{display}
						</span>
					</Tooltip>
				);
			},
		},
		{
			title: "작업",
			key: "actions",
			width: 88,
			render: (_, record) => (
				<Space wrap size="small">
					{(record.status || "대기") !==
						"승인" && (
						<Button
							type="link"
							size="small"
							onClick={() =>
								navigate(
									`/utils/cash-disbursement/edit/${record.id}`,
								)
							}>
							수정
						</Button>
					)}
				</Space>
			),
		},
	];

	const expandedRowRender = (record) => (
		<div
			style={{
				padding: "8px 0 16px",
				maxWidth: "100%",
			}}>
			<Descriptions
				bordered
				size="small"
				column={{ xs: 1, sm: 1, md: 2 }}
				style={{ marginBottom: 16 }}>
				<Descriptions.Item label="일자" span={2}>
					{record.document_date}
				</Descriptions.Item>
				<Descriptions.Item label="작성일시">
					{record.created_at
						? dayjs(
								record.created_at,
							).format(
								"YYYY-MM-DD HH:mm:ss",
							)
						: "—"}
				</Descriptions.Item>
				<Descriptions.Item label="소속 팀" span={2}>
					{record.team_in_charge}
				</Descriptions.Item>
				<Descriptions.Item label="담당자" span={2}>
					{record.person_in_charge}
				</Descriptions.Item>
				<Descriptions.Item label="결제방식" span={2}>
					{record.payment_method || "카드"}
				</Descriptions.Item>
				<Descriptions.Item label="계좌번호" span={2}>
					{record.payment_method === "계좌이체" &&
					(
						record.bank_account_number || ""
					).trim()
						? record.bank_account_number
						: "—"}
				</Descriptions.Item>
				<Descriptions.Item
					label="합계(원화 환산)"
					span={4}>
					{record.total_amount_krw != null &&
					record.total_amount_krw !== ""
						? `${Number(record.total_amount_krw).toLocaleString("ko-KR")} 원`
						: "—"}
				</Descriptions.Item>
			</Descriptions>
			<Text
				strong
				style={{ display: "block", marginBottom: 8 }}>
				적요 · 금액
			</Text>
			<Table
				size="small"
				pagination={false}
				rowKey={(r) =>
					r.id ?? `${r.subject}-${r.amount}`
				}
				dataSource={record.lines || []}
				columns={lineColumns}
				style={{ marginBottom: 16 }}
			/>
			<Text
				strong
				style={{ display: "block", marginBottom: 8 }}>
				영수증 첨부
			</Text>
			{(record.attachments || []).length === 0 ? (
				<Text type="secondary">첨부 파일 없음</Text>
			) : (
				<Space
					direction="vertical"
					size={4}
					align="start">
					{(record.attachments || []).map(
						(att) => (
							<Button
								key={att.id}
								type="link"
								size="small"
								style={{
									padding: 0,
									height: "auto",
								}}
								onClick={() =>
									downloadVoucherAttachment(
										record.id,
										att.id,
										att.original_filename,
									).catch(
										() =>
											message.error(
												"파일을 받지 못했습니다.",
											),
									)
								}>
								{
									att.original_filename
								}
							</Button>
						),
					)}
				</Space>
			)}
		</div>
	);

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="지출결의서 작성 내역" />
			<Layout style={{ minHeight: "100vh" }}>
				<CashDisbursementStaffNavigation />
				<Content className="app-page-content">
					<div
						className="page"
						style={{
							maxWidth: 980,
							margin: "0 auto",
							width: "100%",
						}}>
						<PageHeader title="지출결의서 작성 내역" />
						<div className="surface-card">
							<Tabs
								activeKey={statusTab}
								onChange={setStatusTab}
								items={STATUS_TABS.map(
									({
										key,
										label,
									}) => ({
										key,
										label,
									}),
								)}
								style={{
									marginBottom: 16,
								}}
							/>
							<Table
								rowKey="id"
								columns={columns}
								dataSource={
									filteredRows
								}
								loading={loading}
								scroll={{ x: 880 }}
								sticky={{ offsetHeader: 64 }}
								expandable={{
									expandedRowRender,
									rowExpandable:
										() =>
											true,
									expandRowByClick: true,
								}}
							/>
						</div>
					</div>
				</Content>
			</Layout>
		</ConfigProvider>
	);
};

export default MyCashDisbursementVouchers;
